import { Menu, MenuItem } from '../../components/ui/menu'
import React, { type ReactNode, useEffect, useMemo, useState } from 'react'
import { Layout } from '../../components/Layout'
import { useInterval } from '../../hooks/useInterval'
import { Toasts } from '../../components/toaster/Toast'
import { useTableHooksFromStore } from '../../packages/blueprint-components/src/useTableHooks'
import { axiosGet, axiosPatch } from '../../util/api'
import { Popover } from '../../components/ui/popover'
import { Button } from '../../components/ui/button'
import {
    type AgGridColumnDef,
    AgGridTable,
    DummyHeader,
    type DummyHeaderProps,
} from '../../components/table2/AgGridTable'
import type { CellProps } from '../../components/table2/ad_grid_columns'
import { HeaderContextMenu } from '../../components/table2/react_data_grid_columns'
import { observer } from 'mobx-react-lite'
import { TableStore } from '../../components/table2/table_store'
import { userStore } from '../../stores/user_store'
import { useSavedFiltersStore } from '../../hooks/useSavedFiltersStore'

export const AdminRecipes = observer(() => {
    const [countdown, setCountdown] = useState<number>(5)
    const [store] = useState<TableStore<StuckRecipe>>(new TableStore(userStore, 'AdminRecipes'))
    const savedFiltersStore = useSavedFiltersStore()

    const load = (params: any) => {
        return axiosGet('admin/recipes', params)
    }

    const tableHooks = useTableHooksFromStore(load, {}, false, store, undefined, {
        requireProfile: false,
    })

    useInterval(() => {
        console.log(countdown)
        if (countdown == 0) {
            setCountdown(5)
        } else {
            setCountdown(countdown - 1)
        }
    }, 1000) // 1s

    useEffect(() => {
        if (countdown == 0) {
            tableHooks.reload()
        }
    }, [countdown])

    const columns = useMemo<AgGridColumnDef<StuckRecipe>[]>(
        () => [
            {
                field: 'recipe_id',
                headerName: 'Recipe Id',
                headerComponent: agGridComponent(DummyHeader),
            },
            {
                field: 'user_id',
                headerComponent: agGridComponent(StaticHeader),
            },
            {
                field: 'email',
                headerComponent: agGridComponent(StaticHeader),
                width: 250,
            },
            {
                field: 'profile_id',
                headerComponent: agGridComponent(StaticHeader),
            },
            {
                field: 'created_at',
                headerName: 'Running for',
                headerComponent: agGridComponent(StaticHeader),
                width: 130,
                cellRenderer: agGridComponent(RunningForCellRenderer),
            },
            {
                field: '__actions',
                headerName: 'Actions',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(ActionsCellRenderer),
            },
        ],
        [],
    )

    const reloadButton = (
        <Button
            className="ml-2 whitespace-nowrap"
            disabled={tableHooks.loading || countdown == 0}
            text={`Reload Now (${countdown}s)`}
            intent="primary"
            onClick={() => {
                setCountdown(5)
                tableHooks.reload()
            }}
        />
    )

    return (
        <Layout primary="Recipes" layoutClassName="p-0">
            <AgGridTable
                name={'Recipes'}
                showHeader={false}
                tableHooks={tableHooks}
                store={store}
                savedFiltersStore={savedFiltersStore}
                columnSelection={false}
                search
                columns={columns}
                dateSelect={false}
                otherActions={reloadButton}
            />
        </Layout>
    )
})

const getCurrentUnixTime = (): number => {
    return Math.round(Date.now() / 1000)
}

const StaticHeader = observer(
    <T,>({ column: { colDef }, context: { store } }: DummyHeaderProps<T>) => (
        <HeaderContextMenu columnName={colDef.field as string} store={store}>
            <div className="w-full flex-col">
                <div className="flex items-center">{String(colDef.headerName ?? colDef.field)}</div>
            </div>
        </HeaderContextMenu>
    ),
)

const agGridComponent = (component: unknown): ReactNode => component as ReactNode

const RunningForCellRenderer = ({ data }: CellProps<StuckRecipe>) => {
    return `${getCurrentUnixTime() - data.created_at}s`
}

const ActionsCellRenderer = ({ data }: CellProps<StuckRecipe>) => {
    const clearProgress = () => {
        if (!data.uuid) {
            Toasts.error('Cannot clear this marker because its execution ID is missing.')
            return
        }

        axiosPatch(
            'admin/recipes/clear-progress',
            {
                recipeId: data.recipe_id,
                profileId: data.profile_id,
                linkScope: data.link_scope,
                requestUuid: data.uuid,
            },
            false,
            { ignore: true },
        )
            .then((response) => {
                Toasts.success(response.data.message)
            })
            .catch((error) => {
                console.log(error)
                Toasts.error(error.response?.data?.message ?? error.message)
            })
    }

    const menu = useMemo(() => {
        return (
            <Menu>
                <MenuItem
                    disabled={!data.uuid}
                    text={data.uuid ? 'Clear Stale Progress' : 'Cannot clear: execution ID missing'}
                    onClick={clearProgress}
                />
            </Menu>
        )
    }, [data])

    return (
        <Popover content={menu} position="bottom" interactionKind="hover">
            <Button icon="wrench" text="Actions" />
        </Popover>
    )
}

interface StuckRecipe {
    recipe_id: string
    user_id: number
    email: string
    profile_id: string
    link_scope: string
    uuid?: string
    created_at: number
    __actions?: string
}
